import React, { useState, useEffect, useRef, useMemo } from "react";
import QRCode from "qrcode";
import { TYPES } from "../../constants/wineTypes";
import { Pill } from "../../components/ui/Pill";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import { BrandingModal } from "./components/BrandingModal";
import { WineImportModal } from "./components/WineImportModal";
import { AnalyticsComingSoon } from "./components/AnalyticsComingSoon";
import { FeederPanel, Feeder, DEFAULT_FEEDER } from "./components/FeederPanel";
import { useGeneration } from "../../hooks/useGeneration";
import { useEstablishment } from "../../hooks/useEstablishment";
import { useCustomers } from "../../hooks/useCustomers";
import { buildPublicCarteUrl } from "../../lib/publicCarte";
import type { Wine } from "../../hooks/useWines";
import type { Brand } from "./carte.types";
import {
  addCarteWine,
  addCarteWinesBulk,
  deleteCarteWine,
  updateCarteAdminEmail,
  updateCarteBrand,
  updateCarteWine,
} from "./cartePage.api";

interface CartePageProps {
  wines: Wine[];
  setWines: React.Dispatch<React.SetStateAction<Wine[]>>;
  maison: string;
  setMaison: (v: string) => void;
  updated: string;
  setUpdated: (v: string) => void;
  brand: Brand;
  setBrand: React.Dispatch<React.SetStateAction<Brand>>;
  onPublic: () => void;
  adminEmail: string;
  setAdminEmail: React.Dispatch<React.SetStateAction<string>>;
}

export function CartePage({
  wines,
  setWines,
  maison,
  setMaison,
  updated,
  setUpdated,
  brand,
  setBrand,
  onPublic,
  adminEmail,
  setAdminEmail,
}: CartePageProps) {
  const [adminTab, setAdminTab] = useState("cave");
  const [showAdd, setShowAdd] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ type: "rouge", name: "", mil: "", dom: "", prix: "" });
  const [genAll, setGenAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const qrRef = useRef<HTMLCanvasElement>(null);
  const { generateCarteDesc } = useGeneration();
  const { saveEstablishment } = useEstablishment();
  const { customers, fetchCustomers } = useCustomers();

  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const publicUrl = selectedCustomer
    ? buildPublicCarteUrl({
        customerHash: selectedCustomer.publicCarteHash,
        userId: selectedCustomer.userId,
      })
    : "";

  useEffect(() => {
    fetchCustomers().catch(() => {});
  }, [fetchCustomers]);

  useEffect(() => {
    if (!selectedCustomerId && customers.length === 1) {
      setSelectedCustomerId(customers[0].id);
    }
  }, [customers, selectedCustomerId]);

  useEffect(() => {
    if (qrRef.current && publicUrl) {
      QRCode.toCanvas(qrRef.current, publicUrl, { width: 86, margin: 1, color: { dark: "#1a0a06", light: "#ffffff" } });
    } else if (qrRef.current) {
      qrRef.current.getContext("2d")?.clearRect(0, 0, qrRef.current.width, qrRef.current.height);
    }
  }, [publicUrl]);

  const establishmentSaveTimeout = useRef<number | null>(null);
  useEffect(() => {
    if (!maison.trim()) return;

    if (establishmentSaveTimeout.current) window.clearTimeout(establishmentSaveTimeout.current);
    establishmentSaveTimeout.current = window.setTimeout(() => {
      saveEstablishment(maison).catch(() => {});
    }, 800);

    return () => {
      if (establishmentSaveTimeout.current) window.clearTimeout(establishmentSaveTimeout.current);
    };
  }, [maison, saveEstablishment]);

  const copyPublicLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const sortedByType = useMemo(() => {
    const map: Record<string, Wine[]> = {};
    for (const w of wines) {
      (map[w.type] = map[w.type] || []).push(w);
    }
    for (const k of Object.keys(map)) {
      map[k] = [...map[k]].sort((a, b) => Number(b.highlighted) - Number(a.highlighted));
    }
    return map;
  }, [wines]);

  const genWine = async (id: string) => {
    setWines((ws) => ws.map((x) => (x.id === id ? { ...x, gen: true } : x)));
    try {
      const text = await generateCarteDesc(id);
      setWines((ws) => ws.map((x) => (x.id === id ? { ...x, desc: text, gen: false } : x)));
      setUpdated(new Date().toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));
    } catch {
      setWines((ws) => ws.map((x) => (x.id === id ? { ...x, gen: false } : x)));
    }
  };

  const genAllFn = async () => {
    setGenAll(true);
    for (const w of wines.filter((x) => !x.desc)) await genWine(w.id);
    setGenAll(false);
  };

  const markUpdated = () => {
    setUpdated(new Date().toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));
  };

  const brandSaveTimeout = useRef<number | null>(null);
  const brandSaveSeq = useRef(0);

  const handleBrandUpdate = (nextBrand: Brand) => {
    setBrand(nextBrand);

    if (brandSaveTimeout.current) window.clearTimeout(brandSaveTimeout.current);
    const seq = ++brandSaveSeq.current;
    brandSaveTimeout.current = window.setTimeout(async () => {
      try {
        const savedBrand = await updateCarteBrand(nextBrand);
        if (seq === brandSaveSeq.current) setBrand(savedBrand);
      } catch {}
    }, 600);
  };

  const handleAdminEmailChange = async (email: string) => {
    const previousEmail = adminEmail;
    setAdminEmail(email);
    try {
      const savedEmail = await updateCarteAdminEmail(email);
      setAdminEmail(savedEmail);
    } catch {
      setAdminEmail(previousEmail);
    }
  };

  const handleWineAdd = async (input: { type: string; name: string; mil: string; dom: string; prix: string }) => {
    const createdWine = await addCarteWine(input);
    setWines((ws) => [...ws, createdWine]);
    markUpdated();
  };

  const handleWineUpdate = async (id: string, partial: Partial<Wine>) => {
    const previousWines = wines;
    setWines((ws) => ws.map((wine) => (wine.id === id ? { ...wine, ...partial } : wine)));
    try {
      const updatedWine = await updateCarteWine(id, partial);
      setWines((ws) => ws.map((wine) => (wine.id === id ? { ...updatedWine, gen: wine.gen } : wine)));
      markUpdated();
      return updatedWine;
    } catch {
      setWines(previousWines);
      return undefined;
    }
  };

  const toggleHighlight = async (w: Wine) => { try { await handleWineUpdate(w.id, { highlighted: !w.highlighted }); } catch {} };
  const toggleVisible = async (w: Wine) => { try { await handleWineUpdate(w.id, { visible: !w.visible }); } catch {} };
  const removeWine = async (w: Wine) => {
    const previousWines = wines;
    setWines((ws) => ws.filter((wine) => wine.id !== w.id));
    try {
      await deleteCarteWine(w.id);
      markUpdated();
    } catch {
      setWines(previousWines);
    }
  };

  const feeder = brand.feeder || DEFAULT_FEEDER;

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      {showBranding && <BrandingModal brand={brand} onSave={handleBrandUpdate} onClose={() => setShowBranding(false)} />}
      {showImport && (
        <WineImportModal
          onClose={() => setShowImport(false)}
          onImport={async (rows) => {
            const createdWines = await addCarteWinesBulk(rows);
            if (createdWines.length) {
              setWines((ws) => [...ws, ...createdWines]);
              markUpdated();
            }
            return createdWines.length;
          }}
        />
      )}

      <nav role="tablist" className="px-5 border-b border-border flex gap-0 shrink-0">
        {[{ id: "cave", label: "Cave" }, { id: "settings", label: "Personnalisation" }, { id: "analytics", label: "Analytics QR" }].map((t) => (
          <button key={t.id} role="tab" aria-selected={adminTab === t.id} onClick={() => setAdminTab(t.id)}
            className={`px-4.5 py-3.5 min-h-[48px] border-none bg-transparent cursor-pointer font-sans text-[13px] font-medium transition-all duration-200 ${adminTab === t.id ? "text-foreground border-b-2 border-accent" : "text-secondary border-b-2 border-transparent"}`}>
            {t.label}
            {t.id === "analytics" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-success/[0.12] text-success rounded-lg border border-success/20 align-middle">Bientôt</span>}
            {t.id === "settings" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-accent/[0.12] text-accent rounded-lg border border-accent/20 align-middle">V5</span>}
          </button>
        ))}
      </nav>

      {adminTab === "analytics" && <AnalyticsComingSoon />}

      {adminTab === "settings" && (
        <div className="flex flex-col">
          <FeederPanel
            feeder={feeder}
            onChange={(f) => handleBrandUpdate({ ...brand, feeder: f })}
          />
          <div className="max-w-[720px] mx-auto px-5 pb-12 w-full">
            <div className="card p-4.5 mb-4">
              <label className="label">Email admin (réception commandes)</label>
              <input type="email" value={adminEmail} onChange={(e) => handleAdminEmailChange(e.target.value)} placeholder="admin@mon-domaine.fr" className="input" />
              <div className="text-[11px] text-tertiary italic mt-1.5">Mode test : bons en console (F12)</div>
            </div>
            <div className="card p-4.5">
              <div className="flex justify-between items-center mb-3.5">
                <label className="label !mb-0">Apparence carte publique</label>
                <Pill active onClick={() => setShowBranding(true)} style={{ padding: "7px 14px", fontSize: 12 }}>Personnaliser →</Pill>
              </div>
              <div className="rounded-md border border-border p-4 flex items-center gap-3" style={{ background: brand.bgColor }}>
                {brand.logo && <img src={brand.logo} alt="" className="w-8 h-8 object-contain rounded-md" />}
                <div>
                  <div className="text-[10px] tracking-[2px] uppercase opacity-60" style={{ color: brand.textColor }}>Carte des Vins</div>
                  <div className="text-base font-light" style={{ color: brand.textColor }}>{maison || "Mon Établissement"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {adminTab === "cave" && (
        <div className="flex-1 overflow-auto">
          <div className="max-w-[720px] mx-auto px-5 pt-4 pb-12">
            <div className="mb-3 card px-3.5 py-2.5">
              <label className="label">Établissement</label>
              <input value={maison} onChange={(e) => setMaison(e.target.value)} placeholder="Nom de la maison"
                className="input-underline text-base font-light" />
            </div>

            <div className="flex gap-2 mb-3">
              {[
                { v: wines.length, l: "Vins" },
                { v: wines.filter((w) => w.desc).length, l: "Fiches IA" },
                { v: TYPES.filter((t) => wines.some((w) => w.type === t.id)).length, l: "Catégories" },
              ].map((s) => (
                <div key={s.l} className="stat-card">
                  <div className="text-xl font-medium text-accent leading-none">{s.v}</div>
                  <div className="text-[9px] tracking-[1.5px] text-tertiary uppercase mt-1">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-3.5 flex-wrap">
              <Pill active={showAdd} onClick={() => setShowAdd((s) => !s)} style={{ flex: "1 1 130px" }}>+ Ajouter un vin</Pill>
              <button
                onClick={() => setShowImport(true)}
                className="flex-1 min-w-[130px] py-2.5 rounded-[7px] text-[11px] font-sans tracking-wide bg-transparent border border-border text-secondary cursor-pointer hover:border-accent-border hover:text-foreground transition-colors"
              >
                Importer CSV
              </button>
              <button onClick={genAllFn} disabled={genAll || wines.every((w) => w.desc)}
                className={`flex-1 min-w-[150px] py-2.5 rounded-[7px] text-[11px] font-sans tracking-wide bg-gold-bg border border-gold/20 ${genAll || wines.every((w) => w.desc) ? "cursor-not-allowed text-foreground/20" : "cursor-pointer text-gold"}`}>
                {genAll ? "Génération…" : "Générer toutes les fiches"}
              </button>
            </div>

            {showAdd && (
              <div className="card border-t-2 border-t-accent rounded-t-none p-4 mb-3.5">
                <div className="grid grid-cols-2 gap-x-4">
                  <div className="col-span-full"><label className="label">Appellation</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Gevrey-Chambertin 1er Cru" className="input-underline" /></div>
                  <div><label className="label">Domaine</label><input value={form.dom} onChange={(e) => setForm((f) => ({ ...f, dom: e.target.value }))} placeholder="Rossignol-Trapet" className="input-underline" /></div>
                  <div><label className="label">Millésime</label><input value={form.mil} onChange={(e) => setForm((f) => ({ ...f, mil: e.target.value }))} placeholder="2019" className="input-underline" /></div>
                  <div><label className="label">Catégorie</label><select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="input-underline cursor-pointer">{TYPES.map((t) => <option key={t.id} value={t.id} className="bg-surface">{t.label}</option>)}</select></div>
                  <div><label className="label">Prix (€)</label><input value={form.prix} onChange={(e) => setForm((f) => ({ ...f, prix: e.target.value }))} placeholder="45" className="input-underline" /></div>
                </div>
                <Pill active onClick={async () => {
                  if (form.name.trim() && form.dom.trim() && form.mil.trim()) {
                    await handleWineAdd(form);
                    setForm({ type: "rouge", name: "", mil: "", dom: "", prix: "" });
                    setShowAdd(false);
                  }
                }} style={{ marginTop: 14, width: "100%" }}>Ajouter à la carte</Pill>
              </div>
            )}

            {TYPES.filter((t) => sortedByType[t.id]?.length).map((type) => (
              <div key={type.id} className="mb-2.5">
                <div className="flex items-center gap-2 py-[7px] pb-1">
                  <div className="w-3.5 h-px opacity-50" style={{ background: type.color }} />
                  <span className="text-[8px] tracking-[3px] uppercase opacity-70" style={{ color: type.color }}>{type.label}</span>
                  <div className="flex-1 h-px bg-foreground/[0.04]" />
                </div>
                {sortedByType[type.id].map((w) => (
                  <div
                    key={w.id}
                    className={`flex items-center gap-2.5 px-3 py-2.5 card rounded-[7px] mb-1 transition-opacity ${w.visible ? "" : "opacity-50"} ${w.highlighted ? "ring-1 ring-gold/35 shadow-[0_0_0_1px_rgba(196,154,60,0.10)]" : ""}`}
                  >
                    {w.highlighted ? (
                      <span title="Mis en avant" className="shrink-0 text-gold leading-none">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.95L22 9.8l-5.4 4.65L18.18 22 12 18.4 5.82 22l1.58-7.55L2 9.8l7.1-.85L12 2z" /></svg>
                      </span>
                    ) : (
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${w.desc ? "shadow-[0_0_5px_rgb(var(--color-accent))]" : ""}`} style={{ background: w.desc ? "rgb(var(--color-accent))" : "rgba(255,255,255,0.14)" }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-light text-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                        {w.name} <span className="text-tertiary text-[11px]">{w.mil}</span>
                        {!w.visible && <span className="ml-2 text-[9px] tracking-[1px] uppercase text-tertiary border border-foreground/[0.08] rounded px-1 py-px align-middle">masqué</span>}
                      </div>
                      <div className="text-[10px] text-tertiary mt-px">{w.dom}</div>
                    </div>
                    <span className="text-[9px] tracking-[1px] uppercase shrink-0 w-[50px] text-center overflow-hidden text-ellipsis whitespace-nowrap opacity-60" style={{ color: type.color }}>{type.short}</span>
                    <span className="text-[12px] text-gold shrink-0 w-[34px] text-right">{w.prix}€</span>
                    <button onClick={() => genWine(w.id)} disabled={w.gen}
                      className={`px-3 py-1.5 min-h-[36px] rounded-[5px] text-[11px] font-sans tracking-wide min-w-[58px] ${w.gen ? "cursor-not-allowed bg-transparent border border-foreground/[0.05] text-foreground/[0.18]" : w.desc ? "cursor-pointer bg-accent-bg border border-accent-border text-accent" : "cursor-pointer bg-transparent border border-border text-secondary"}`}>
                      {w.gen ? "…" : w.desc ? "Regen" : "Générer"}
                    </button>

                    <DropdownMenu
                      positioning={{ placement: "bottom-end" }}
                      trigger={
                        <button
                          aria-label="Actions sur ce vin"
                          className="w-10 h-10 rounded-md flex items-center justify-center cursor-pointer bg-transparent border border-foreground/[0.07] text-secondary shrink-0 hover:border-foreground/20 transition-colors"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="5" cy="12" r="1.6" />
                            <circle cx="12" cy="12" r="1.6" />
                            <circle cx="19" cy="12" r="1.6" />
                          </svg>
                        </button>
                      }
                      items={[
                        { value: "highlight", label: w.highlighted ? "Retirer de la mise en avant" : "Mettre en avant", color: "#C49A3C", onSelect: () => toggleHighlight(w) },
                        { value: "visibility", label: w.visible ? "Masquer (invisible)" : "Rendre visible", color: "#7A9FB5", onSelect: () => toggleVisible(w) },
                        { value: "delete", label: "Supprimer", color: "#C95151", onSelect: () => removeWine(w) },
                      ]}
                    />
                  </div>
                ))}
              </div>
            ))}

            {wines.length === 0 && <div className="text-center py-12 text-tertiary text-sm italic">Aucun vin</div>}

            <div className="card rounded-[9px] p-4 mt-1">
              <div className="text-[11px] tracking-[3px] text-secondary/70 uppercase mb-3">QR Code</div>
              <label className="label">Client destinataire</label>
              <select
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
                disabled={customers.length === 0}
                className="input-underline mb-3 cursor-pointer"
              >
                <option value="" className="bg-surface">
                  {customers.length === 0 ? "Aucun client disponible" : "Sélectionner un client"}
                </option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id} className="bg-surface">
                    {customer.name} · {customer.email}
                  </option>
                ))}
              </select>
              <div className="flex gap-3.5 items-start">
                <div className="bg-white p-1.5 rounded-[5px] shrink-0"><canvas ref={qrRef} /></div>
                <div className="flex-1">
                  <div className="text-[10px] text-secondary italic mb-2 break-all">
                    {publicUrl ? publicUrl.replace(/^https?:\/\//, "") : "Sélectionnez un client pour générer son lien."}
                  </div>
                  <div className="flex gap-1.5 mb-2">
                    <div className="flex-1 py-[7px] bg-accent-bg border border-accent-border rounded-[7px] text-center">
                      <div className="text-lg font-medium text-accent leading-none">{wines.length}</div>
                      <div className="text-[8px] tracking-[1.5px] text-tertiary uppercase mt-0.5">Vins</div>
                    </div>
                    <div className="flex-[2] py-[7px] px-3 bg-gold-bg border border-gold/15 rounded-[7px]">
                      <div className="text-[11px] text-gold">{updated}</div>
                      <div className="text-[8px] tracking-[1.5px] text-tertiary uppercase mt-0.5">Dernière màj</div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mb-2">
                    <Pill disabled={!publicUrl} onClick={copyPublicLink} style={{ flex: 1 }}>{copied ? "Lien copié ✓" : "Copier le lien"}</Pill>
                  </div>
                  <Pill active onClick={onPublic} style={{ width: "100%" }}>Voir la carte publique →</Pill>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
